import React, { useCallback, useEffect, useMemo, useState } from 'react';

const emptyForm = {
  klijent: '',
  grad: '',
  adresa: '',
  datum_montaze: '',
  dimenzija_ekrana: '',
  model_modula: '',
  raspored_modula: '',
  broj_modula: '',
  serija_modula: '',
  model_modula_2: '',
  model_kartice: '',
  broj_kartica: '',
  kontroler: '',
  server: '',
};

const fields = [
  ['klijent', 'Klijent'],
  ['grad', 'Grad'],
  ['adresa', 'Adresa'],
  ['datum_montaze', 'Datum montaže'],
  ['dimenzija_ekrana', 'Dimenzija ekrana'],
  ['model_modula', 'Model modula'],
  ['raspored_modula', 'Raspored modula'],
  ['broj_modula', 'Broj modula'],
  ['serija_modula', 'Serija modula'],
  ['model_modula_2', 'Oznaka modula'],
  ['model_kartice', 'Model kartice'],
  ['broj_kartica', 'Broj kartica'],
  ['kontroler', 'Kontroler'],
  ['server', 'Server'],
];

const styles = {
  page: {
    minHeight: '100%',
    background: '#050a1a',
    color: '#fff',
    padding: '16px 14px 32px',
    boxSizing: 'border-box',
    fontFamily: 'Arial, sans-serif',
  },
  panel: {
    background: '#0a132b',
    border: '1px solid #1a2a4a',
    borderRadius: 14,
    padding: 14,
    marginBottom: 12,
  },
  input: {
    width: '100%',
    boxSizing: 'border-box',
    border: '1px solid #30456e',
    borderRadius: 9,
    background: '#101d3b',
    color: '#fff',
    padding: '13px 12px',
    fontSize: 16,
    outline: 'none',
  },
  primary: {
    width: '100%',
    border: '1px solid #527aff',
    borderRadius: 10,
    background: '#2855d9',
    color: '#fff',
    padding: '14px 16px',
    fontSize: 16,
    fontWeight: 700,
    cursor: 'pointer',
  },
  secondary: {
    border: '1px solid #30456e',
    borderRadius: 9,
    background: '#162447',
    color: '#fff',
    padding: '11px 14px',
    fontSize: 14,
    cursor: 'pointer',
  },
  label: {
    display: 'block',
    color: '#b7c9ed',
    fontSize: 13,
    fontWeight: 700,
    marginBottom: 6,
  },
};

export default function MobilniLEDEkrani() {
  const [screens, setScreens] = useState([]);
  const [sifarnik, setSifarnik] = useState({});
  const [sifarnikError, setSifarnikError] = useState('');
  const [activeSuggestion, setActiveSuggestion] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [success, setSuccess] = useState('');

  const loadScreens = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch('/api/led-ekrani/', {
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });
      if (response.status === 401 || response.status === 403) {
        throw new Error('Sesija je istekla. Prijavite se ponovo.');
      }
      if (!response.ok) throw new Error('Greška pri učitavanju LED ekrana.');
      const data = await response.json();
      setScreens(data.screens || []);
    } catch (err) {
      setError(err.message || 'Greška pri učitavanju ekrana.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSifarnik = useCallback(async () => {
    setSifarnikError('');
    try {
      const response = await fetch('/api/led-sifarnik/', {
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) {
        throw new Error('Nije moguće učitati šifarnik.');
      }
      const data = await response.json();
      setSifarnik(data);
    } catch (err) {
      setSifarnikError(err.message || 'Greška pri učitavanju šifarnika.');
    }
  }, []);

  useEffect(() => {
    loadScreens();
    loadSifarnik();
  }, [loadScreens, loadSifarnik]);

  const filtered = useMemo(() => {
    const q = search.trim().toLocaleLowerCase('sr');
    if (!q) return screens;
    return screens.filter((screen) =>
      Object.values(screen).some((value) =>
        String(value ?? '').toLocaleLowerCase('sr').includes(q)
      )
    );
  }, [screens, search]);

  const changeForm = (event) => {
    setForm((current) => ({ ...current, [event.target.name]: event.target.value }));
  };

  const submit = async (event) => {
    event.preventDefault();
    setFormError('');
    setSuccess('');
    if (!form.klijent.trim() || !form.grad.trim()) {
      setFormError('Klijent i grad su obavezni.');
      return;
    }

    setSaving(true);
    try {
      const csrfResponse = await fetch('/api/led-csrf-token/', {
        method: 'GET',
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });
      if (!csrfResponse.ok) {
        throw new Error(csrfResponse.status === 403
          ? 'Server je odbio zahtev za CSRF token. Proveri prijavu.'
          : 'Nije moguće preuzeti CSRF token.');
      }
      const csrfData = await csrfResponse.json();
      if (!csrfData.csrfToken) throw new Error('Server nije vratio CSRF token.');

      let datumZaUpis = form.datum_montaze;
      if (/^\d{4}-\d{2}-\d{2}$/.test(datumZaUpis)) {
        const [godina, mesec, dan] = datumZaUpis.split('-');
        datumZaUpis = `${dan}.${mesec}.${godina}`;
      }

      const response = await fetch('/api/led-ekrani/dodaj/', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfData.csrfToken,
        },
        body: JSON.stringify({ ...form, datum_montaze: datumZaUpis }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result.error || (response.status === 403
          ? 'Server je odbio zahtev. Proveri CSRF zaštitu.'
          : 'Nije uspelo dodavanje ekrana.'));
      }
      setShowAdd(false);
      setForm({ ...emptyForm });
      setSuccess(`Ekran broj ${result.r_br} je uspešno dodat.`);
      await loadScreens();
    } catch (err) {
      setFormError(err.message || 'Greška pri upisu ekrana.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.page}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
        <div style={{ width: 46, height: 46, borderRadius: 12, background: '#2855d9', display: 'grid', placeItems: 'center', fontSize: 22, fontWeight: 800 }}>E</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: 0.4 }}>ECOPELL</div>
          <div style={{ color: '#9eb5e8', fontSize: 13, marginTop: 3 }}>Mobilna evidencija LED ekrana</div>
        </div>
        <button type="button" onClick={loadScreens} aria-label="Osveži ekrane" style={{ ...styles.secondary, padding: '10px 12px' }}>↻</button>
      </header>

      <div style={{ ...styles.panel, padding: 12 }}>
        <div style={{ fontSize: 13, color: '#9eb5e8', marginBottom: 5 }}>UKUPNO EKRANA</div>
        <div style={{ fontSize: 28, fontWeight: 800 }}>{screens.length}</div>
      </div>

      {success && <div role="status" style={{ ...styles.panel, color: '#a7f3d0', background: '#123b2b', borderColor: '#24694d' }}>{success}</div>}

      <div style={{ marginBottom: 12 }}>
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Pretraži klijenta, grad, adresu..."
          aria-label="Pretraga LED ekrana"
          style={styles.input}
        />
      </div>

      <button type="button" onClick={() => { setForm({ ...emptyForm }); setFormError(''); setShowAdd(true); }} style={{ ...styles.primary, marginBottom: 16 }}>
        + Dodaj LED ekran
      </button>

      {loading && <p style={{ color: '#9eb5e8', textAlign: 'center', padding: 20 }}>Učitavanje LED ekrana...</p>}
      {!loading && error && <div style={{ ...styles.panel, color: '#fecaca' }}>{error}<button type="button" onClick={loadScreens} style={{ ...styles.secondary, marginTop: 12, width: '100%' }}>Pokušaj ponovo</button></div>}

      {!loading && !error && (
        <div style={{ display: 'grid', gap: 11 }}>
          {filtered.map((screen, index) => (
            <button
              key={`${screen.r_br}-${index}`}
              type="button"
              onClick={() => setSelected(screen)}
              style={{ ...styles.panel, margin: 0, width: '100%', textAlign: 'left', color: '#fff', cursor: 'pointer' }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ color: '#7da2ff', fontSize: 12, fontWeight: 700, marginBottom: 5 }}>EKRAN BR. {screen.r_br || index + 1}</div>
                  <div style={{ fontSize: 17, fontWeight: 800, overflowWrap: 'anywhere' }}>{screen.klijent || 'Bez naziva klijenta'}</div>
                </div>
                <span style={{ color: '#9eb5e8', fontSize: 22, lineHeight: 1 }}>›</span>
              </div>
              <div style={{ color: '#c3d2ef', fontSize: 14, marginTop: 10 }}>{screen.grad || 'Grad nije naveden'}</div>
              {screen.adresa && <div style={{ color: '#9eb5e8', fontSize: 13, marginTop: 4, overflowWrap: 'anywhere' }}>{screen.adresa}</div>}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginTop: 12 }}>
                {screen.dimenzija_ekrana && <span style={{ background: '#162447', border: '1px solid #30456e', borderRadius: 7, padding: '6px 8px', fontSize: 12 }}>Dimenzija: {screen.dimenzija_ekrana}</span>}
                {screen.datum_montaze && <span style={{ background: '#162447', border: '1px solid #30456e', borderRadius: 7, padding: '6px 8px', fontSize: 12 }}>Montaža: {screen.datum_montaze}</span>}
              </div>
            </button>
          ))}
          {filtered.length === 0 && <div style={{ ...styles.panel, textAlign: 'center', color: '#9eb5e8' }}>Nema pronađenih ekrana.</div>}
        </div>
      )}

      {selected && (
        <div onClick={() => setSelected(null)} style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,.82)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <section onClick={(event) => event.stopPropagation()} style={{ width: '100%', maxHeight: '88dvh', overflowY: 'auto', background: '#0a132b', border: '1px solid #30456e', borderRadius: '18px 18px 0 0', padding: 18, boxSizing: 'border-box', paddingBottom: 'calc(18px + env(safe-area-inset-bottom))' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 16 }}>
              <div><div style={{ color: '#7da2ff', fontSize: 12, fontWeight: 700 }}>EKRAN BR. {selected.r_br}</div><h2 style={{ fontSize: 21, margin: '5px 0 0', overflowWrap: 'anywhere' }}>{selected.klijent || 'LED ekran'}</h2></div>
              <button type="button" onClick={() => setSelected(null)} style={styles.secondary}>Zatvori</button>
            </div>
            <div style={{ display: 'grid', gap: 9 }}>
              {fields.map(([key, label]) => (
                <div key={key} style={{ background: '#101d3b', border: '1px solid #1a2a4a', borderRadius: 9, padding: 12 }}>
                  <div style={{ color: '#9eb5e8', fontSize: 12, marginBottom: 5 }}>{label}</div>
                  <div style={{ fontSize: 15, overflowWrap: 'anywhere' }}>{selected[key] || '—'}</div>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}

      {showAdd && (
        <div onClick={() => !saving && setShowAdd(false)} style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'rgba(0,0,0,.84)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <section onClick={(event) => event.stopPropagation()} style={{ width: '100%', maxHeight: '94dvh', overflowY: 'auto', background: '#0a132b', border: '1px solid #30456e', borderRadius: '18px 18px 0 0', padding: 18, boxSizing: 'border-box', paddingBottom: 'calc(18px + env(safe-area-inset-bottom))' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}><h2 style={{ margin: 0, fontSize: 21 }}>Dodaj LED ekran</h2><button type="button" disabled={saving} onClick={() => setShowAdd(false)} style={styles.secondary}>Zatvori</button></div>
            <form onSubmit={submit}>
              <div style={{ display: 'grid', gap: 13 }}>
                {fields.map(([name, label]) => {
                  const listId = {
                    klijent: 'mobile-led-klijenti',
                    model_modula: 'mobile-led-modeli-modula',
                    model_modula_2: 'mobile-led-oznake-modula',
                    serija_modula: 'mobile-led-serije-modula',
                    model_kartice: 'mobile-led-modeli-kartica',
                    kontroler: 'mobile-led-kontroleri',
                    server: 'mobile-led-serveri',
                  }[name];

                  const sifarnikKey = {
                    klijent: 'klijenti',
                    model_modula: 'modeli_modula',
                    model_modula_2: 'oznake_modula',
                    serija_modula: 'serije_modula',
                    model_kartice: 'modeli_kartica',
                    kontroler: 'kontroleri',
                    server: 'serveri',
                  }[name];

                  const options = listId ? (sifarnik[sifarnikKey] || []) : [];
                  const filteredOptions = options.filter((option) =>
                    String(option).toLocaleLowerCase('sr').includes(
                      String(form[name] || '').toLocaleLowerCase('sr')
                    )
                  );

                  return (
                    <label key={name} style={{ display: 'block', position: 'relative' }}>
                      <span style={styles.label}>
                        {label}{(name === 'klijent' || name === 'grad') ? ' *' : ''}
                      </span>
                      <input
                        type={name === 'datum_montaze' ? 'date' : 'text'}
                        name={name}
                        value={form[name]}
                        onChange={(event) => {
                          changeForm(event);
                          if (listId) setActiveSuggestion(name);
                        }}
                        onFocus={() => listId && setActiveSuggestion(name)}
                        onClick={() => listId && setActiveSuggestion(name)}
                        style={styles.input}
                        disabled={saving}
                        required={name === 'klijent' || name === 'grad'}
                        placeholder={name === 'datum_montaze' ? undefined : label}
                        autoComplete="off"
                      />
                      {listId && activeSuggestion === name && options.length > 0 && (
                        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1200, maxHeight: 220, overflowY: 'auto', background: '#101d3b', border: '1px solid #527aff', borderRadius: 9, marginTop: 4, boxShadow: '0 8px 24px rgba(0,0,0,.45)' }}>
                          {filteredOptions.length > 0 ? filteredOptions.map((option) => (
                            <button
                              key={option}
                              type="button"
                              onMouseDown={(event) => event.preventDefault()}
                              onClick={() => {
                                changeForm({ target: { name, value: option } });
                                setActiveSuggestion('');
                              }}
                              style={{ display: 'block', width: '100%', padding: '12px', textAlign: 'left', color: '#fff', background: 'transparent', border: 0, borderBottom: '1px solid #26395f', fontSize: 15, cursor: 'pointer' }}
                            >
                              {option}
                            </button>
                          )) : (
                            <div style={{ padding: 12, color: '#9eb5e8', fontSize: 14 }}>Nema poklapanja. Možeš uneti sopstvenu vrednost.</div>
                          )}
                        </div>
                      )}
                    </label>
                  );
                })}
              </div>
              {sifarnikError && (
                <div role="status" style={{ ...styles.panel, color: '#fde68a', marginTop: 14 }}>
                  {sifarnikError} Unos i dalje možeš da popuniš ručno.
                  <button type="button" onClick={loadSifarnik} disabled={saving} style={{ ...styles.secondary, width: '100%', marginTop: 10 }}>
                    Ponovo učitaj šifarnik
                  </button>
                </div>
              )}
              {formError && <div role="alert" style={{ ...styles.panel, color: '#fecaca', marginTop: 14 }}>{formError}</div>}
              <button type="submit" disabled={saving} style={{ ...styles.primary, marginTop: 16, opacity: saving ? 0.65 : 1 }}>{saving ? 'Upisivanje...' : 'Sačuvaj ekran'}</button>
            </form>
          </section>
        </div>
      )}
    </div>
  );
}
